import { useMemo, useCallback } from 'react';
import { CellClassParams, DisplayedColumnsChangedEvent, BodyScrollEvent } from 'ag-grid-community';
import type { AgGridReactProps } from 'ag-grid-react';

const useAgGridProps = (): Partial<AgGridReactProps> => {
  const getCellClass = useCallback((params: CellClassParams) => {
    const classes: string[] = [];

    if (params.node.level === 1) {
      const displayedColumns = params.api.getAllDisplayedColumns();
      const nonPinnedColumns = displayedColumns.filter((col) => !col.getPinned());

      if (nonPinnedColumns.length > 0) {
        if (nonPinnedColumns[0].getColId() === params.column.getColId()) {
          classes.push('custom-first-visible');
        }
        if (nonPinnedColumns[nonPinnedColumns.length - 1].getColId() === params.column.getColId()) {
          classes.push('custom-last-visible');
        }
      }
    }

    return classes;
  }, []);

  const onDisplayedColumnsChanged = useCallback((params: DisplayedColumnsChangedEvent) => {
    params.api.refreshCells({ force: true });
  }, []);

  const onBodyScroll = useCallback((params: BodyScrollEvent) => {
    // Only refresh when horizontal scroll occurs (column virtualization)
    if (params.direction === 'horizontal') {
      params.api.refreshCells({ force: true });
    }
  }, []);

  return useMemo<Partial<AgGridReactProps>>(
    () => ({
      debug: true,
      rowModelType: 'serverSide',
      treeData: true,
      rowHeight: 72,
      suppressServerSideFullWidthLoadingRow: true,
      suppressContextMenu: true,
      animateRows: false,
      suppressMaxRenderedRowRestriction: true,
      suppressMoveWhenColumnDragging: true,
      tooltipShowMode: 'whenTruncated',
      tooltipShowDelay: 0,
      scrollbarWidth: 8,
      alwaysShowHorizontalScroll: true,
      alwaysShowVerticalScroll: true,
      pagination: false,
      onDisplayedColumnsChanged,
      onBodyScroll,
      defaultColDef: {
        filter: false,
        resizable: true,
        minWidth: 120,
        flex: 1,
        suppressMovable: true,
        suppressNavigable: true,
        headerComponentParams: {
          enablePinning: false,
        },
        cellClass: getCellClass,
      },
    }),
    [getCellClass, onDisplayedColumnsChanged, onBodyScroll],
  );
};
export default useAgGridProps;
